import { useEffect, useMemo } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Link } from "react-router-dom";
import { REGION_COORDS, formatListingPrice, formatMapPrice } from "@/lib/constants";
import ListingImage from "@/components/ListingImage";

const DEFAULT_CENTER = [40.85, -73.88];

function jitter(id = "") {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  const dx = ((h % 1000) / 1000 - 0.5) * 0.05;
  const dy = (((h >>> 10) % 1000) / 1000 - 0.5) * 0.05;
  return [dx, dy];
}

function priceIcon(price, listingType = "sale") {
  const label = formatMapPrice(price, listingType);
  // Approximate chip size from label length — iconSize [1,1] was clipping text to "$3".
  const width = Math.max(44, Math.min(88, 12 + label.length * 7));
  const height = 22;
  return L.divIcon({
    className: "wg-price-pin",
    html: `<div class="wg-price-pin__label">${label}</div>`,
    iconSize: [width, height],
    iconAnchor: [Math.round(width / 2), Math.round(height / 2)],
  });
}

function FitBounds({ points }) {
  const map = useMap();
  useEffect(() => {
    if (!points.length) return;
    if (points.length === 1) {
      map.setView([points[0].lat, points[0].lng], 13, { animate: false });
      return;
    }
    const bounds = L.latLngBounds(points.map((p) => [p.lat, p.lng]));
    map.fitBounds(bounds.pad(0.12), { animate: false, maxZoom: 14 });
  }, [map, points]);
  return null;
}

export default function ListingsMap({
  properties = [],
  heightClass = "h-[50vh] md:h-[70vh]",
  compact = false,
}) {
  const points = useMemo(() => properties.map((p) => {
    if (typeof p.lat === "number" && typeof p.lng === "number") {
      return { ...p, lat: p.lat, lng: p.lng };
    }
    const base = REGION_COORDS[p.region] || DEFAULT_CENTER;
    const [dx, dy] = jitter(p.id);
    return { ...p, lat: base[0] + dx, lng: base[1] + dy };
  }), [properties]);

  const center = points.length ? [points[0].lat, points[0].lng] : DEFAULT_CENTER;

  return (
    <div
      className={`overflow-hidden rounded-sm border border-white/10 ${heightClass}`}
      data-testid="listings-map"
    >
      <MapContainer
        center={center}
        zoom={11}
        scrollWheelZoom={!compact}
        style={{ height: "100%", width: "100%", background: "#0B0C10" }}
      >
        <TileLayer
          url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
          attribution='&copy; OpenStreetMap contributors &copy; CARTO'
        />
        <FitBounds points={points} />
        {points.map((p) => (
          <Marker key={p.id} position={[p.lat, p.lng]} icon={priceIcon(p.price, p.listing_type)}>
            <Popup>
              <div style={{ width: compact ? 160 : 180 }}>
                <ListingImage
                  src={p.images?.[0]}
                  alt={p.title}
                  style={{ width: "100%", height: 88, objectFit: "cover", borderRadius: 2, marginBottom: 6 }}
                />
                <div style={{ fontWeight: 600, fontSize: 13 }}>{formatListingPrice(p.price, p.listing_type)}</div>
                <div style={{ fontSize: 12, color: "#444" }}>{p.title}</div>
                <div style={{ fontSize: 11, color: "#666", marginBottom: 4 }}>
                  {p.bedrooms === 0 ? "Studio" : `${p.bedrooms} bd`} · {p.bathrooms} ba · {p.region}
                </div>
                {p.near_transit && (
                  <div style={{ fontSize: 10, color: "#9a7b16", marginBottom: 4 }}>
                    Near transit{p.transit_note ? `: ${p.transit_note}` : ""}
                  </div>
                )}
                {p.walk_in && (
                  <div style={{ fontSize: 10, color: "#666", marginBottom: 4 }}>Walk-in / few steps</div>
                )}
                <Link to={`/listings/${p.id}`} style={{ fontSize: 12, color: "#9a7b16", fontWeight: 600 }}>
                  View details →
                </Link>
              </div>
            </Popup>
          </Marker>
        ))}
      </MapContainer>
    </div>
  );
}
